import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// ResourceSearchPalette — ⌘K-triggered overlay that searches across
// every resource the caller can see, matching name/description/URL/
// category in one go. Results group by category, highlight the
// matched term, and respect keyboard nav (↑↓ ↵ Esc, ⌘+↵ for new
// tab, ⌘+P to pin to Quick access).
//
// Parent (Resources page) owns the open state, the resources list,
// and the pinned resource ids — the palette is purely presentational
// + delegates pin actions back via callback.
import { useEffect, useMemo, useRef, useState } from "react";
import { safeHref } from "../lib/safeUrl";
// Best-effort host extraction — pulls "example.com" from a URL so
// the row can hint where it goes. Falls back to the raw URL when
// parsing fails (e.g. an internal app:// scheme).
function hostOf(url) {
    try {
        return new URL(url).host.replace(/^www\./, "");
    }
    catch {
        return url;
    }
}
// Wrap each occurrence of `q` in the source string in <mark> so the
// match is visually obvious. Case-insensitive. Falls through to the
// raw text when `q` is empty.
function highlight(text, q) {
    if (!q)
        return text;
    const lower = text.toLowerCase();
    const ql = q.toLowerCase();
    const parts = [];
    let i = 0;
    while (i < text.length) {
        const at = lower.indexOf(ql, i);
        if (at === -1) {
            parts.push(text.slice(i));
            break;
        }
        if (at > i)
            parts.push(text.slice(i, at));
        parts.push(_jsx("mark", { className: "resources-search-mark", children: text.slice(at, at + ql.length) }, `m-${at}`));
        i = at + ql.length;
    }
    return _jsx(_Fragment, { children: parts });
}
// Group label + ordering. Known categories get a friendly label; the
// fallback is the raw category string titlecased.
const GROUP_LABELS = {
    documents: "Documents",
    chats: "Chats",
    applications: "Applications",
    sops: "SOPs",
    tools: "Tools",
};
const GROUP_ORDER = ["documents", "chats", "applications", "sops", "tools"];
function groupKeyOf(r) {
    return r.category || "other";
}
function groupLabelOf(key) {
    return (GROUP_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1));
}
export default function ResourceSearchPalette({ open, resources, pinnedIds, onClose, onTogglePin, onCreateFromQuery, onOpen, tools = [], }) {
    const [query, setQuery] = useState("");
    const [activeIdx, setActiveIdx] = useState(0);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    // Reset on open so each invocation starts clean. Focus the input
    // after the modal mounts — without this the user has to click
    // before typing.
    useEffect(() => {
        if (!open)
            return;
        setQuery("");
        setActiveIdx(0);
        const id = window.setTimeout(() => inputRef.current?.focus(), 30);
        return () => window.clearTimeout(id);
    }, [open]);
    // Match against title + description + url + category. Pinned
    // resources sort to the top of their group so frequently-used
    // tools dominate. Score is dumb-and-fast — substring containment
    // weighted by which field matched (title > category > url > desc).
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q)
            return resources;
        function score(r) {
            const t = r.title.toLowerCase();
            const c = r.category.toLowerCase();
            const u = r.url.toLowerCase();
            const d = (r.description ?? "").toLowerCase();
            let s = 0;
            if (t.startsWith(q))
                s += 50;
            if (t.includes(q))
                s += 30;
            if (c.includes(q))
                s += 12;
            if (u.includes(q))
                s += 6;
            if (d.includes(q))
                s += 4;
            if (pinnedIds.has(r.id))
                s += 5;
            return s;
        }
        return resources
            .map((r) => ({ r, s: score(r) }))
            .filter((x) => x.s > 0)
            .sort((a, b) => b.s - a.s)
            .map((x) => x.r);
    }, [resources, query, pinnedIds]);
    // Tools (Lead CRM, Key Contacts, …) matched against the query the same
    // dumb-substring way as resources. Shown at the very top of the palette.
    const filteredTools = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q)
            return tools;
        return tools.filter((t) => `${t.label} ${t.keywords ?? ""}`.toLowerCase().includes(q));
    }, [tools, query]);
    // Build the flat entry list (headers + rows) for rendering and for
    // keyboard nav. We keep the section header rows in the array but
    // they're not navigable — `nav` skips them.
    const entries = useMemo(() => {
        const out = [];
        // Tools group first — quick jumps to the CRM / Key Contacts boards.
        if (filteredTools.length > 0) {
            out.push({ kind: "header", label: "Tools" });
            for (const t of filteredTools) {
                out.push({ kind: "row", label: t.label, tool: t });
            }
        }
        if (filtered.length === 0)
            return out;
        // Group + order — pinned at top, then GROUP_ORDER, then anything else.
        const byKey = new Map();
        for (const r of filtered) {
            const key = pinnedIds.has(r.id) ? "__pinned__" : groupKeyOf(r);
            const arr = byKey.get(key) ?? [];
            arr.push(r);
            byKey.set(key, arr);
        }
        const orderedKeys = [];
        if (byKey.has("__pinned__"))
            orderedKeys.push("__pinned__");
        for (const k of GROUP_ORDER)
            if (byKey.has(k))
                orderedKeys.push(k);
        for (const k of byKey.keys()) {
            if (!orderedKeys.includes(k))
                orderedKeys.push(k);
        }
        for (const k of orderedKeys) {
            out.push({
                kind: "header",
                label: k === "__pinned__" ? "Quick access" : groupLabelOf(k),
            });
            for (const r of byKey.get(k) ?? []) {
                out.push({ kind: "row", label: r.title, resource: r });
            }
        }
        return out;
    }, [filtered, pinnedIds, filteredTools]);
    // Indices in `entries` that are navigable rows (skip headers).
    const navIdxs = useMemo(() => entries.flatMap((e, i) => (e.kind === "row" ? [i] : [])), [entries]);
    // Clamp activeIdx after entries change.
    useEffect(() => {
        if (activeIdx >= navIdxs.length) {
            setActiveIdx(Math.max(0, navIdxs.length - 1));
        }
    }, [activeIdx, navIdxs.length]);
    function activeResource() {
        if (navIdxs.length === 0)
            return null;
        const idx = navIdxs[Math.min(activeIdx, navIdxs.length - 1)];
        return entries[idx]?.resource ?? null;
    }
    function activeEntry() {
        if (navIdxs.length === 0)
            return null;
        return entries[navIdxs[Math.min(activeIdx, navIdxs.length - 1)]] ?? null;
    }
    function openInTab(r, newTab) {
        // Run every URL through safeHref before navigation — both
        // window.open and assigning to window.location.href will execute a
        // `javascript:` URI, and `rel="noopener noreferrer"` on the
        // window.open features string does NOT block that. safeHref returns
        // "#" for unsafe schemes, which we drop instead of opening.
        const safe = safeHref(r.url);
        if (!safe || safe === "#")
            return;
        onOpen?.(r);
        if (newTab) {
            window.open(safe, "_blank", "noopener,noreferrer");
        }
        else {
            window.location.href = safe;
        }
        onClose();
    }
    function onKeyDown(e) {
        if (e.key === "Escape") {
            e.preventDefault();
            onClose();
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIdx((i) => Math.min(i + 1, Math.max(0, navIdxs.length - 1)));
            return;
        }
        if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIdx((i) => Math.max(0, i - 1));
            return;
        }
        if (e.key === "Enter") {
            e.preventDefault();
            const entry = activeEntry();
            if (entry?.tool) {
                entry.tool.onSelect();
                onClose();
                return;
            }
            if (entry?.resource) {
                // Resources always open in a new tab (built-in tools are the
                // exception — they run onSelect to navigate in-app, handled above).
                openInTab(entry.resource, true);
                return;
            }
            if (onCreateFromQuery && query.trim()) {
                onCreateFromQuery(query.trim());
                onClose();
            }
            return;
        }
        // ⌘+P or Ctrl+P → toggle pin on the active row. We swallow the
        // browser's print shortcut here since the palette has focus.
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "p") {
            e.preventDefault();
            const r = activeResource();
            if (r)
                onTogglePin(r);
        }
    }
    // Keep the active row scrolled into view as the user navigates with
    // the arrow keys, so long lists stay usable.
    useEffect(() => {
        if (!open || navIdxs.length === 0)
            return;
        const idx = navIdxs[Math.min(activeIdx, navIdxs.length - 1)];
        const el = listRef.current?.querySelector(`[data-entry-idx="${idx}"]`);
        if (el)
            el.scrollIntoView({ block: "nearest" });
    }, [activeIdx, navIdxs, open]);
    if (!open)
        return null;
    let navCursor = 0; // increments each time we render a row
    return (_jsx("div", { className: "modal-backdrop resources-search-backdrop", onClick: onClose, children: _jsxs("div", { className: "resources-search-palette", role: "dialog", "aria-label": "Search resources", onClick: (e) => e.stopPropagation(), onKeyDown: onKeyDown, children: [_jsxs("div", { className: "resources-search-input-row", children: [_jsx("span", { "aria-hidden": "true", children: "\uD83D\uDD0D" }), _jsx("input", { ref: inputRef, value: query, onChange: (e) => {
                                setQuery(e.target.value);
                                setActiveIdx(0);
                            }, placeholder: "Search by name, description, URL, or category\u2026", autoComplete: "off", spellCheck: false }), _jsxs("span", { className: "resources-search-count muted small", children: [filtered.length + filteredTools.length, " ", filtered.length + filteredTools.length === 1
                                    ? "result"
                                    : "results"] }), _jsx("kbd", { className: "resources-search-kbd", children: "ESC" })] }), _jsx("div", { className: "resources-search-list", ref: listRef, children: entries.length === 0 ? (_jsxs("div", { className: "resources-search-empty muted small", children: ["No resources match \"", query, "\"."] })) : (entries.map((e, i) => {
                        if (e.kind === "header") {
                            return (_jsx("div", { className: "resources-search-group", children: e.label }, `h-${i}`));
                        }
                        if (e.tool) {
                            const t = e.tool;
                            const toolActive = navIdxs[activeIdx] === i;
                            const cursorAt = navCursor;
                            const toolRet = (_jsxs("div", { "data-entry-idx": i, className: `resources-search-row ${toolActive ? "active" : ""}`, onMouseEnter: () => setActiveIdx(cursorAt), onClick: () => {
                                    t.onSelect();
                                    onClose();
                                }, title: t.label, children: [_jsx("span", { className: "resources-search-row-icon", "aria-hidden": "true", children: t.icon }), _jsxs("span", { className: "resources-search-row-body", children: [_jsx("span", { className: "resources-search-row-title", children: highlight(t.label, query) }), _jsx("span", { className: "resources-search-row-meta muted small", children: "Open tool" })] }), _jsx("span", { className: "muted small", children: "Tool" }), toolActive && (_jsx("kbd", { className: "resources-search-kbd", children: "\u21B5" }))] }, `tool-${t.id}`));
                            navCursor += 1;
                            return toolRet;
                        }
                        const r = e.resource;
                        const isActive = navIdxs[activeIdx] === i;
                        const pinned = pinnedIds.has(r.id);
                        const buName = r.business_unit_names?.[0];
                        const ret = (_jsxs("div", { "data-entry-idx": i, className: `resources-search-row ${isActive ? "active" : ""}`, onMouseEnter: () => setActiveIdx(navCursor), onClick: () => openInTab(r, true), title: r.url, children: [_jsx("span", { className: "resources-search-row-icon", "aria-hidden": "true", children: r.icon || (pinned ? "📌" : "🔗") }), _jsxs("span", { className: "resources-search-row-body", children: [_jsx("span", { className: "resources-search-row-title", children: highlight(r.title, query) }), _jsxs("span", { className: "resources-search-row-meta muted small", children: [hostOf(r.url), r.description && r.description.trim() && (_jsxs(_Fragment, { children: [" \u00B7 ", highlight(r.description.slice(0, 60), query)] }))] })] }), buName && (_jsx("span", { className: "pill resources-search-row-bu", children: buName })), _jsx("span", { className: "muted small", children: groupLabelOf(groupKeyOf(r)) }), _jsx("button", { type: "button", className: `resources-search-pin ${pinned ? "active" : ""}`, onClick: (ev) => {
                                        ev.stopPropagation();
                                        onTogglePin(r);
                                    }, title: pinned ? "Unpin from Quick access" : "Pin to Quick access", "aria-pressed": pinned, "aria-label": pinned ? "Unpin resource" : "Pin resource", children: "\uD83D\uDCCC" }), isActive && _jsx("kbd", { className: "resources-search-kbd", children: "\u21B5" })] }, r.id));
                        navCursor += 1;
                        return ret;
                    })) }), onCreateFromQuery && query.trim() && filtered.length === 0 && (_jsxs("div", { className: "resources-search-create", children: [_jsx("span", { "aria-hidden": "true", children: "\uD83D\uDCA1" }), _jsxs("span", { children: ["Nothing fits? Press", " ", _jsx("kbd", { className: "resources-search-kbd", children: "\u21B5" }), " to add \"", query.trim(), "\" as a new resource."] })] })), _jsxs("div", { className: "resources-search-footer muted small", children: [_jsxs("span", { children: [_jsx("kbd", { className: "resources-search-kbd", children: "\u2191\u2193" }), " navigate"] }), _jsxs("span", { children: [_jsx("kbd", { className: "resources-search-kbd", children: "\u21B5" }), " open in new tab"] }), _jsxs("span", { style: { marginLeft: "auto" }, children: [_jsx("kbd", { className: "resources-search-kbd", children: "\u2318P" }), " pin to Quick access"] })] })] }) }));
}
